import { useEffect, useState } from "react";
import { getJSON, postJSON, putJSON } from "../services/api";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { GroupGuard } from "../auth/GroupGuard";

const REQUIRED_GROUPS = [
  "bcba776b-ad66-4c3c-b253-a10a1b65c294", // Admins - TI
  "1c4b0acb-3657-48c1-9024-0904307a94b7", // CEP - Admins
];

type Motivo = { id_motivo: number; descricao: string; ativo: string };

export default function MotivosPage() {
  const [motivos, setMotivos] = useState<Motivo[]>([]);
  const [loading, setLoading] = useState(false);
  const [editando, setEditando] = useState<Motivo | null>(null);
  const [descricao, setDescricao] = useState("");

  async function carregar() {
    setLoading(true);
    try {
      const data = await getJSON("/motivos");
      setMotivos(Array.isArray(data) ? data : []);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { carregar(); }, []);

  function abrirNovo() {
    setEditando({ id_motivo: 0, descricao: "", ativo: "S" });
    setDescricao("");
  }

  function abrirEditar(m: Motivo) {
    setEditando(m);
    setDescricao(m.descricao);
  }

  async function salvar() {
    if (!editando) return;
    if (editando.id_motivo === 0) {
      await postJSON("/motivos", { descricao });
    } else {
      await putJSON(`/motivos/${editando.id_motivo}`, { descricao });
    }
    setEditando(null);
    carregar();
  }

  async function toggle(m: Motivo) {
    const novo = m.ativo === "S" ? "N" : "S";
    await putJSON(`/motivos/${m.id_motivo}/toggle`, { ativo: novo });
    carregar();
  }

  return (
    <GroupGuard allowedGroups={REQUIRED_GROUPS}>
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h1 className="text-xl font-semibold">Gestão de Motivos</h1>
        <Button onClick={abrirNovo}>+ Novo Motivo</Button>
      </div>

      {loading ? (
        <div>Carregando...</div>
      ) : (
        <table className="w-full text-sm border border-slate-200 rounded-lg overflow-hidden">
        <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wide">
          <tr>
            <th className="px-4 py-3 text-left">Descrição</th>
            <th className="px-4 py-3 text-center">Status</th>
            <th className="px-4 py-3 text-center">Ações</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-200">
          {motivos.map((m) => (
            <tr key={m.id_motivo} className="hover:bg-slate-50 transition-colors">
              <td className="px-4 py-3 font-medium text-slate-800">{m.descricao}</td>
              <td className="px-4 py-3 text-center">
                {m.ativo === "S" ? (
                  <span className="bg-green-100 text-green-700 px-3 py-1 rounded-full text-xs font-medium">
                    Ativo
                  </span>
                ) : (
                  <span className="bg-red-100 text-red-700 px-3 py-1 rounded-full text-xs font-medium">
                    Inativo
                  </span>
                )}
              </td>
              <td className="px-4 py-3 flex justify-center gap-2">
                <Button
                  variant="outline"
                  onClick={() => abrirEditar(m)}
                  className="text-xs"
                >
                  ✏️ Editar
                </Button>
                {m.ativo === "S" ? (
                  <Button
                    variant="destructive"
                    onClick={() => toggle(m)}
                    className="text-xs"
                  >
                    ⛔ Desativar
                  </Button>
                ) : (
                  <Button
                    variant="outline"
                    onClick={() => toggle(m)}
                    className="text-xs"
                  >
                    ✅ Ativar
                  </Button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      )}

      <Dialog open={!!editando} onOpenChange={() => setEditando(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editando?.id_motivo === 0 ? "Novo Motivo" : "Editar Motivo"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <input
              className="w-full border rounded px-2 py-1"
              placeholder="Descrição"
              value={descricao}
              onChange={(e) => setDescricao(e.target.value)}
            />
            <Button onClick={salvar}>Salvar</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
    </GroupGuard>
  );
}